/**
 * usePartnerData Hook
 * 협력사 데이터 관리 훅
 */
import { useState, useEffect, useCallback } from 'react';
import { partnerAccountingApi } from '../api/PartnerAccountingApi';
import { PartnerListItem, PartnerNameItem, PartnerSearchFilters } from '../types/AccountingPartner.types';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';
import { loadFilters, saveFilters } from '@/shared/utils/filterStorage';

export const usePartnerData = () => {
    const { showToast } = useToast();

    // 필터 상태 - 저장된 값 복원
    const savedFilters = loadFilters<{
        searchPartnerId: string;
        searchPartnerIdInput: string;
        searchStatus: string;
        searchWithdrawalStatus: string; // 출금상태 필터 추가
    }>('partnerAccountingFilters');

    const [searchPartnerId, setSearchPartnerId] = useState(savedFilters?.searchPartnerId || '');
    const [searchPartnerIdInput, setSearchPartnerIdInput] = useState(savedFilters?.searchPartnerIdInput || '');
    const [searchStatus, setSearchStatus] = useState(savedFilters?.searchStatus || '');
    const [searchWithdrawalStatus, setSearchWithdrawalStatus] = useState(savedFilters?.searchWithdrawalStatus || ''); // 출금상태 필터 추가
    const [allData, setAllData] = useState<PartnerListItem[]>([]);
    const [partnerData, setPartnerData] = useState<PartnerListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [partnerNames, setPartnerNames] = useState<PartnerNameItem[]>([]);
    const ITEMS_PER_PAGE = 10;

    // 페이지 데이터 업데이트 (클라이언트 사이드 페이지네이션)
    const updatePageData = useCallback((data: PartnerListItem[], page: number) => {
        const startIndex = (page - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        const pageData = data.slice(startIndex, endIndex);

        setPartnerData(pageData);
        setTotalPages(Math.ceil(data.length / ITEMS_PER_PAGE));
        setCurrentPage(page);

    }, [ITEMS_PER_PAGE]);

    // 협력사 목록 조회
    const fetchPartnerList = useCallback(async () => {
        try {
            setLoading(true);

            const response = await partnerAccountingApi.getPartnerList();

            if (Array.isArray(response)) {
                setAllData(response);
                updatePageData(response, 1);
            } else {
                setAllData([]);
                setPartnerData([]);
                setTotalPages(1);
            }
        } catch (error) {
            showToast('협력사 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
            setAllData([]);
            setPartnerData([]);
            setTotalPages(1);
        } finally {
            setLoading(false);
        }
    }, [updatePageData, showToast]);

    // 협력사명 목록 조회
    const fetchPartnerNames = useCallback(async () => {
        try {
            const names = await partnerAccountingApi.getPartnerNames();
            setPartnerNames(names);
        } catch (error) {
            setPartnerNames([]);
        }
    }, []);

    // 협력사 검색
    const handleSearch = useCallback(async () => {
        try {
            setLoading(true);
            setCurrentPage(1);

            const searchFilters: PartnerSearchFilters = {};

            if (searchPartnerIdInput && searchPartnerIdInput.trim()) {
                searchFilters.suppId = searchPartnerIdInput.trim();
            } else if (searchPartnerId && searchPartnerId.trim()) {
                searchFilters.suppId = searchPartnerId.trim();
            }

            if (searchStatus && searchStatus.trim()) {
                searchFilters.statusCode = searchStatus.trim();
            }

            // 빈 값 제거 및 필드명 매핑
            const params: Record<string, string> = {};
            if (searchFilters.suppName) params.suppName = searchFilters.suppName;
            if (searchFilters.suppId) params.suppId = searchFilters.suppId;
            if (searchFilters.statusCode) params.statusCode = searchFilters.statusCode;
            if (searchWithdrawalStatus && searchWithdrawalStatus.trim()) params.withdrawalYn = searchWithdrawalStatus.trim(); // 출금상태 필터 추가

            const response = await partnerAccountingApi.searchPartners(params);

            if (Array.isArray(response)) {
                setAllData(response);
                updatePageData(response, 1);
            } else {
                setAllData([]);
                setPartnerData([]);
                setTotalPages(1);
            }

            // 필터 저장
            saveFilters('partnerAccountingFilters', {
                searchPartnerId,
                searchPartnerIdInput,
                searchStatus,
                searchWithdrawalStatus // 출금상태 필터 저장
            });
        } catch (error) {
            showToast('협력사 검색에 실패했습니다.', { type: ToastType.ERROR });
            setAllData([]);
            setPartnerData([]);
            setTotalPages(1);
        } finally {
            setLoading(false);
        }
    }, [searchPartnerIdInput, searchPartnerId, searchStatus, searchWithdrawalStatus, updatePageData, showToast]);

    // 페이지 변경
    const handlePageChange = useCallback((page: number) => {
        updatePageData(allData, page);
    }, [allData, updatePageData]);

    // 출금제한 설정
    const handleWithdrawalLimit = useCallback(async (partnerName: string, suppId: string, currentStatus: string | null, targetAction: 'ALLOW' | 'RESTRICT') => {
        // 타겟 액션에 따라 새 상태와 액션 메시지 결정
        const newStatus: 'Y' | 'N' = targetAction === 'ALLOW' ? 'N' : 'Y';
        const action = targetAction === 'ALLOW' ? '출금허용' : '출금제한';

        const confirmed = window.confirm(
          `${partnerName}을(를) ${action} 하시겠습니까?\n${targetAction === 'RESTRICT' ? '출금제한하시면 해당 협력사와 소속 라이더들은 더 이상 출금할 수 없습니다.' : '출금허용하시면 해당 협력사와 소속 라이더들은 다시 출금할 수 있습니다.'}`
        );

        if (confirmed) {
            try {
                setLoading(true);
                await partnerAccountingApi.updateWithdrawalStatus(suppId, newStatus);
                showToast(`${partnerName} ${action}이 적용되었습니다.`, { type: ToastType.SUCCESS });
                await fetchPartnerList(); // 목록 새로고침
            } catch (error) {
                showToast(`${action} 설정에 실패했습니다.`, { type: ToastType.ERROR });
            } finally {
                setLoading(false);
            }
        }
    }, [fetchPartnerList, showToast]);

    // 검색 조건 초기화
    const resetSearchConditions = useCallback(() => {
        setSearchPartnerId('');
        setSearchPartnerIdInput('');
        setSearchStatus('');
        setSearchWithdrawalStatus(''); // 출금상태 필터 초기화
    }, []);

    // 컴포넌트 마운트 시 데이터 조회
    useEffect(() => {
        // 저장된 필터가 있으면 검색, 없으면 전체 목록 조회
        const saved = loadFilters<{
            searchPartnerId: string;
            searchPartnerIdInput: string;
            searchStatus: string;
            searchWithdrawalStatus: string; // 출금상태 필터 추가
        }>('partnerAccountingFilters');

        if (saved && (saved.searchPartnerId || saved.searchPartnerIdInput || saved.searchStatus || saved.searchWithdrawalStatus)) {
            handleSearch();
        } else {
            fetchPartnerList();
        }
        fetchPartnerNames();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        // 상태
        searchPartnerId,
        setSearchPartnerId,
        searchPartnerIdInput,
        setSearchPartnerIdInput,
        searchStatus,
        setSearchStatus,
        searchWithdrawalStatus, // 출금상태 필터 추가
        setSearchWithdrawalStatus, // 출금상태 필터 setter 추가
        allData,
        partnerData,
        loading,
        currentPage,
        totalPages,
        partnerNames,
        itemsPerPage: ITEMS_PER_PAGE,

        // 함수
        handleSearch,
        handlePageChange,
        handleWithdrawalLimit,
        fetchPartnerList,
        resetSearchConditions
    };
};
